import * as React from "react";
import { Building2, Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/cn";
import type { TenantInfo } from "./types";

export interface TenantSwitcherProps {
  tenants: TenantInfo[];
  current?: TenantInfo;
  onSelect: (tenant: TenantInfo) => void;
  rootDomain?: string;
  className?: string;
}

export function TenantSwitcher({ tenants, current, onSelect, rootDomain = "", className }: TenantSwitcherProps) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (tenants.length <= 1) {
    return (
      <div className={cn("flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/10", className)}>
        <Building2 className="w-3.5 h-3.5 text-indigo-300 flex-shrink-0" />
        <span className="text-xs text-indigo-200 font-medium truncate capitalize">
          {current?.slug ?? "App"}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)} ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
      >
        <Building2 className="w-3.5 h-3.5 text-indigo-300 flex-shrink-0" />
        <span className="flex-1 text-xs text-indigo-200 font-medium truncate capitalize text-left">
          {current?.slug ?? "Select workspace"}
        </span>
        <ChevronDown className={cn("w-3 h-3 text-indigo-300 flex-shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden py-1">
          {tenants.map((t) => (
            <button
              key={t.id}
              onClick={() => { onSelect(t); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="w-6 h-6 rounded-md bg-indigo-600 flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0">
                {t.name.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">{t.name}</p>
                {rootDomain && (
                  <p className="text-[10px] text-gray-400 truncate">{t.slug}.{rootDomain}</p>
                )}
              </div>
              {(t.isDefault || t.id === current?.id) && (
                <Check className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
